import * as Yup from 'yup'
import { Maybe, TaskTypeDataFieldsDynamicZone } from '../../../generated/graphql'
import { parseDot } from '../../../utils'
import { PHONE_NUMBER_REGEX } from '../../../constants'

/**
 * A configured min/max as a number, or undefined when the field has no bound.
 *
 * The task builder saves a blank min/max ("Unlimited") as null, so a missing or
 * non-numeric bound means "no limit on that side" — exactly how the mobile app
 * reads it. `Number('abc')` used to slip through as NaN and make every value
 * fail validation.
 */
export const parseBound = (raw: unknown): number | undefined => {
  if (raw === null || raw === undefined || raw === '') return undefined
  const value = Number(raw)
  return Number.isNaN(value) ? undefined : value
}

/**
 * Schema for a typed number. Blank is empty (null), "1,5" reads as 1.5, and
 * only the configured bounds are enforced, matching the mobile app.
 */
const numberSchema = (rawMin?: unknown, rawMax?: unknown) => {
  let schema = Yup.number()
    .typeError('Please enter a valid number')
    .transform((_value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }
      if (typeof originalValue === 'string') {
        return Number(originalValue.replace(/,/g, '.'))
      }
      return originalValue
    })
    .nullable()

  const min = parseBound(rawMin)
  const max = parseBound(rawMax)
  if (min !== undefined) {
    schema = schema.min(min, `Value must be greater than or equal to ${min}`)
  }
  if (max !== undefined) {
    schema = schema.max(max, `Value must be less than or equal to ${max}`)
  }
  return schema
}

export const validationSchema = (dataFields: Maybe<TaskTypeDataFieldsDynamicZone>[]) => {
  return Yup.object().shape({
    ...dataFields?.reduce((acc, field, index) => {
      const name = `${field?.__typename}_${index}_${parseDot((field as any)?.label || '')}`
      const isRequired = (field as any)?.isRequired || false

      if (isRequired) {
        switch (field?.__typename) {
          case 'ComponentCustomDataFieldsText':
            acc[name] = Yup.string().required('This field is required')
            break
          case 'ComponentCustomDataFieldsDataList':
            acc[name] = Yup.object().shape({
              values: Yup.array().of(Yup.string()).required('This field is required'),
              listId: Yup.string().nullable()
            })
            break
          case 'ComponentCustomDataFieldsDateTimeField':
            acc[name] = Yup.string().required('This field is required')
            break
          case 'ComponentCustomDataFieldsEmailField':
            acc[name] = Yup.string()
              .email('Invalid email format')
              .required('This field is required')
            break
          case 'ComponentCustomDataFieldsImageField':
            acc[name] = Yup.array().of(Yup.object()).required('This field is required')
            break
          case 'ComponentCustomDataFieldsNumberField':
            acc[name] = numberSchema((field as any)?.min, (field as any)?.max).required(
              'This field is required'
            )
            break
          case 'ComponentCustomDataFieldsMetricsSpecific':
            acc[name] = Yup.object().shape({
              value: numberSchema((field as any)?.min, (field as any)?.max).required(
                'This field is required'
              ),
              metric: Yup.string().required('This field is required')
            })
            break
          case 'ComponentCustomDataFieldsPercentage':
            acc[name] = numberSchema(0, 100).required('This field is required')
            break
          case 'ComponentCustomDataFieldsPhoneNumberField':
            acc[name] = Yup.string()
              .required('This field is required')
              .matches(PHONE_NUMBER_REGEX, 'Please enter a valid phone number')
            break
          case 'ComponentCustomDataFieldsRadio':
          case 'ComponentCustomDataFieldsSelect':
            acc[name] = Yup.string().required('This field is required')
            break
          case 'ComponentCustomDataFieldsCheckbox':
            acc[name] = Yup.array().of(Yup.string()).required('This field is required')
            break
          default:
            console.warn(`Unsupported field type for validation: ${field?.__typename}`)
        }
      } else {
        switch (field?.__typename) {
          case 'ComponentCustomDataFieldsText':
            acc[name] = Yup.string()
            break
          case 'ComponentCustomDataFieldsDataList':
            acc[name] = Yup.object().shape({
              values: Yup.array().of(Yup.string()),
              listId: Yup.string().nullable()
            })
            break
          case 'ComponentCustomDataFieldsDateTimeField':
            acc[name] = Yup.string()
            break
          case 'ComponentCustomDataFieldsEmailField':
            acc[name] = Yup.string().email('Invalid email format')
            break
          case 'ComponentCustomDataFieldsImageField':
            acc[name] = Yup.array().of(Yup.object())
            break
          case 'ComponentCustomDataFieldsNumberField':
            acc[name] = numberSchema((field as any)?.min, (field as any)?.max)
            break
          case 'ComponentCustomDataFieldsMetricsSpecific':
            acc[name] = Yup.object().shape({
              value: numberSchema((field as any)?.min, (field as any)?.max),
              metric: Yup.string()
            })
            break
          case 'ComponentCustomDataFieldsPercentage':
            // Shares numberSchema so an optional percentage left blank is valid;
            // a bare Yup.number() cast '' to NaN and blocked the submission.
            acc[name] = numberSchema(0, 100)
            break
          case 'ComponentCustomDataFieldsPhoneNumberField':
            acc[name] = Yup.string().matches(
              PHONE_NUMBER_REGEX,
              'Please enter a valid phone number'
            )
            break
          case 'ComponentCustomDataFieldsRadio':
          case 'ComponentCustomDataFieldsSelect':
            acc[name] = Yup.string()
            break
          case 'ComponentCustomDataFieldsCheckbox':
            acc[name] = Yup.array().of(Yup.string())
            break
          default:
            console.warn(`Unsupported field type for validation: ${field?.__typename}`)
        }
      }
      return acc
    }, {} as Record<string, any>)
  })
}
